import { useAuthStore } from '@/store/authStore'
import { useTabNavigationStore } from '@/store/tabNavigationStore'
import { ru } from '@/i18n/ru'

/**
 * GracePeriodBanner - Shows warning/lock banner when organization exceeds tier limits
 *
 * Orange banner during grace period (7 days warning)
 * Red banner when soft-locked (writes blocked)
 */
export const GracePeriodBanner = () => {
  const subscriptionInfo = useAuthStore((s) => s.subscriptionInfo)
  const { switchTab } = useTabNavigationStore()

  const graceStatus = subscriptionInfo?.GraceStatus
  if (!graceStatus || !graceStatus.is_over_limit) return null

  const isLocked = graceStatus.is_soft_locked

  // Build exceeded resources list
  const exceededList = graceStatus.exceeded_resources
    ? Object.entries(graceStatus.exceeded_resources).map(([key, val]) => {
        const label = ru.gracePeriod?.resourceLabels?.[key as keyof typeof ru.gracePeriod.resourceLabels] || key
        return `${label}: ${val.current}/${val.limit}`
      })
    : []

  const title = isLocked
    ? ru.gracePeriod.lockedTitle
    : ru.gracePeriod.warningTitle

  const message = isLocked
    ? ru.gracePeriod.lockedMessage
    : ru.gracePeriod.warningMessage.replace('{{days}}', String(graceStatus.grace_period_days_remaining))

  // Orange for warning, Red for locked
  const bgColor = isLocked ? '#D32F2F' : '#E65100'
  const bgColorLight = isLocked ? 'rgba(211, 47, 47, 0.12)' : 'rgba(230, 81, 0, 0.12)'
  const textColor = isLocked ? '#D32F2F' : '#E65100'
  const darkBgColor = isLocked ? 'rgba(211, 47, 47, 0.2)' : 'rgba(245, 124, 0, 0.2)'
  const darkTextColor = isLocked ? '#EF9A9A' : '#FFB74D'

  return (
    <div
      className="mx-3 mt-2 mb-1 rounded-xl px-4 py-3"
      style={{
        backgroundColor: `var(--grace-banner-bg, ${bgColorLight})`,
      }}
    >
      <style>{`
        :root { --grace-banner-bg: ${bgColorLight}; --grace-banner-text: ${textColor}; }
        .dark { --grace-banner-bg: ${darkBgColor}; --grace-banner-text: ${darkTextColor}; }
      `}</style>

      <div className="flex items-start gap-2">
        <span className="text-lg mt-0.5">{isLocked ? '\u{1F512}' : '\u{26A0}\u{FE0F}'}</span>
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-semibold"
            style={{ color: 'var(--grace-banner-text)' }}
          >
            {title}
          </p>
          <p
            className="text-xs mt-0.5"
            style={{ color: 'var(--grace-banner-text)', opacity: 0.85 }}
          >
            {message}
          </p>
          {exceededList.length > 0 && (
            <p
              className="text-xs mt-1 font-medium"
              style={{ color: 'var(--grace-banner-text)', opacity: 0.7 }}
            >
              {exceededList.join(' \u2022 ')}
            </p>
          )}
          <button
            className="text-xs font-semibold mt-2 px-3 py-1.5 rounded-lg"
            style={{
              backgroundColor: bgColor,
              color: '#FFFFFF',
            }}
            onClick={() => {
              switchTab('profile')
              // Navigate to subscription plans
              const { pushScreen } = useTabNavigationStore.getState()
              pushScreen('subscription-plans')
            }}
          >
            {ru.gracePeriod.upgradeButton}
          </button>
        </div>
      </div>
    </div>
  )
}
